/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable unicorn/no-null */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const FORCE_VIRTUAL_KEYBOARD = true;

interface VirtualKeyboardContextType {
  isKeyboardVisible: boolean;
  showKeyboard: () => void;
  hideKeyboard: () => void;
  activeInput: HTMLInputElement | HTMLTextAreaElement | null;
  setActiveInput: (input: HTMLInputElement | HTMLTextAreaElement | null) => void;
  currentLanguage: string;
  setCurrentLanguage: (lang: string) => void;
  isNumericInput: boolean;
  isTouchDevice: boolean;
  lockKeyboard: () => void;
  unlockKeyboard: () => void;
  triggerKeyboardForInput: (input: HTMLInputElement | HTMLTextAreaElement) => void;
}

const VirtualKeyboardContext = createContext<VirtualKeyboardContextType | null>(null);

export const useVirtualKeyboard = () => {
  const context = useContext(VirtualKeyboardContext);
  if (!context) {
    throw new Error('useVirtualKeyboard must be used within VirtualKeyboardProvider');
  }
  return context;
};

interface VirtualKeyboardProviderProperties {
  children: React.ReactNode;
}

export function VirtualKeyboardProvider({ children }: VirtualKeyboardProviderProperties) {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [activeInput, setActiveInput] = useState<HTMLInputElement | HTMLTextAreaElement | null>(
    null,
  );
  const [currentLanguage, setCurrentLanguage] = useState('english');
  const [isNumericInput, setIsNumericInput] = useState(false);
  const isInteractingWithKeyboardReference = useRef(false);
  const isKeyboardLockedReference = useRef(false);

  const isTouchDevice =
    FORCE_VIRTUAL_KEYBOARD || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);

  const showKeyboard = useCallback(() => {
    if (isTouchDevice) {
      setIsKeyboardVisible(true);
    }
  }, [isTouchDevice]);

  const hideKeyboard = useCallback(() => {
    if (isKeyboardLockedReference.current) {
      return;
    }
    setIsKeyboardVisible(false);
  }, []);

  const lockKeyboard = useCallback(() => {
    isKeyboardLockedReference.current = true;
  }, []);

  const unlockKeyboard = useCallback(() => {
    isKeyboardLockedReference.current = false;
  }, []);

  const triggerKeyboardForInput = useCallback(
    (input: HTMLInputElement | HTMLTextAreaElement) => {
      if (!isTouchDevice) return;

      const inputType = input.getAttribute('type') || 'text';
      const isNumeric = ['number', 'tel'].includes(inputType);
      setIsNumericInput(isNumeric);

      setActiveInput(input);
      input.focus();
      setIsKeyboardVisible(true);
    },
    [isTouchDevice],
  );

  useEffect(() => {
    if (!isTouchDevice) return;

    const handleMouseDown = (event_: MouseEvent) => {
      const target = event_.target as HTMLElement;
      const keyboardElement = document.querySelector('#virtual-keyboard-container');

      if (keyboardElement?.contains(target)) {
        isInteractingWithKeyboardReference.current = true;
      }
    };

    const handleMouseUp = () => {
      setTimeout(() => {
        isInteractingWithKeyboardReference.current = false;
      }, 200);
    };

    const handleClick = (event_: MouseEvent) => {
      if (isKeyboardLockedReference.current || !isKeyboardVisible) {
        return;
      }

      const target = event_.target as HTMLElement;
      const keyboardElement = document.querySelector('#virtual-keyboard-container');

      if (keyboardElement?.contains(target)) {
        if (target.closest('.language-dropdown')) {
          return;
        }
        return;
      }

      if (target.closest('[data-keyboard-trigger]')) {
        return;
      }

      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

      if (!isInput) {
        hideKeyboard();
        setActiveInput(null);
      }
    };

    const handleFocusOut = (event_: FocusEvent) => {
      setTimeout(() => {
        if (isKeyboardLockedReference.current || isInteractingWithKeyboardReference.current) {
          return;
        }

        const { activeElement } = document;
        const isInputFocused =
          activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA';

        const keyboardElement = document.querySelector('#virtual-keyboard-container');
        const isFocusOnKeyboard = keyboardElement?.contains(activeElement as Node);

        if (!isInputFocused && !isFocusOnKeyboard && isKeyboardVisible) {
          const relatedTarget = event_.relatedTarget as HTMLElement;
          if (relatedTarget && keyboardElement?.contains(relatedTarget)) {
            return;
          }
          hideKeyboard();
          setActiveInput(null);
        }
      }, 150);
    };

    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('click', handleClick);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('click', handleClick);
    };
  }, [isTouchDevice, isKeyboardVisible, hideKeyboard]);

  useEffect(() => {
    const handleKeyDown = (event_: KeyboardEvent) => {
      if (event_.key === 'Escape' && isKeyboardVisible) {
        hideKeyboard();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isKeyboardVisible, hideKeyboard]);

  const contextValue = useMemo(
    () => ({
      isKeyboardVisible,
      showKeyboard,
      hideKeyboard,
      activeInput,
      setActiveInput,
      currentLanguage,
      setCurrentLanguage,
      isNumericInput,
      isTouchDevice,
      lockKeyboard,
      unlockKeyboard,
      triggerKeyboardForInput,
    }),
    [
      isKeyboardVisible,
      showKeyboard,
      hideKeyboard,
      activeInput,
      setActiveInput,
      currentLanguage,
      setCurrentLanguage,
      isNumericInput,
      isTouchDevice,
      lockKeyboard,
      unlockKeyboard,
      triggerKeyboardForInput,
    ],
  );

  return (
    <VirtualKeyboardContext.Provider value={contextValue}>
      {children}
    </VirtualKeyboardContext.Provider>
  );
}

export default VirtualKeyboardProvider;
